<script lang="ts">
  import ModalShell from './ModalShell.svelte';
  import { authRequestCode, authVerifyCode } from './api';

  let {
    onClose,
    onSuccess,
    title = 'Sign in',
    subline,
  }: {
    onClose: () => void;
    /** Run after a successful verify instead of reloading the page.
     *  Caller is responsible for closing the modal and any follow-up
     *  navigation. Useful for chaining sign-in + an immediate action
     *  (e.g. claim this trip into the user's dashboard). */
    onSuccess?: () => Promise<void> | void;
    /** Override the modal title — e.g. "Save to dashboard" when the
     *  user is being asked to sign in to complete a specific action. */
    title?: string;
    /** Optional second-line subhead under the email field on step 1. */
    subline?: string;
  } = $props();

  type Step =
    | { kind: 'email' }
    | { kind: 'code'; email: string };
  let step: Step = $state({ kind: 'email' });
  let email: string = $state('');
  let code: string = $state('');
  let busy: boolean = $state(false);
  let codeInputEl: HTMLInputElement | null = $state(null);

  // Focus the code input when we transition to the code step. Safari's
  // one-time-code autofill suggestion appears on focus if a matching
  // OTP email arrived recently — auto-focusing puts the user inside
  // that window without an extra tap.
  $effect(() => {
    if (step.kind === 'code') {
      // Yield a microtask so the input has mounted before focus().
      queueMicrotask(() => codeInputEl?.focus());
    }
  });
  let error: string | null = $state(null);

  async function onSendCode(e: SubmitEvent) {
    e.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    if (!trimmed) {
      error = 'Enter your email.';
      return;
    }
    busy = true;
    error = null;
    try {
      await authRequestCode(trimmed);
      step = { kind: 'code', email: trimmed };
      code = '';
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not send code';
    } finally {
      busy = false;
    }
  }

  async function onVerify(e: SubmitEvent) {
    e.preventDefault();
    if (busy || step.kind !== 'code') return;
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      error = 'Enter the 6-digit code.';
      return;
    }
    busy = true;
    error = null;
    try {
      await authVerifyCode(step.email, trimmed);
      if (onSuccess) {
        // Caller takes over — they're chaining a follow-up action and
        // a full reload would lose the in-flight context.
        await onSuccess();
      } else {
        // Full reload so the cookie middleware picks up the new
        // session and any owner_id-aware affordances re-render
        // against it. The root route's cookie redirect then forwards
        // to /u/<token>.
        window.location.reload();
      }
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not verify code';
      busy = false;
    }
  }

  function backToEmail() {
    step = { kind: 'email' };
    code = '';
    error = null;
  }
</script>

<ModalShell {title} {onClose} role="dialog" maxWidth={400}>
  {#if step.kind === 'email'}
    <form onsubmit={onSendCode} class="form">
      {#if subline}
        <p class="lede"><strong>{subline}</strong></p>
      {/if}
      <p class="lede">
        Enter your email and we'll send a 6-digit code. New email? We'll create your account.
      </p>
      <label class="field">
        <span class="label">Email</span>
        <input
          type="email"
          bind:value={email}
          autocomplete="email"
          inputmode="email"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          required
          disabled={busy}
        />
      </label>
      {#if error}
        <p class="error">{error}</p>
      {/if}
      <button class="btn btn-primary submit" type="submit" disabled={busy || email.trim() === ''}>
        {busy ? 'Sending…' : 'Send code'}
      </button>
    </form>
  {:else}
    <form onsubmit={onVerify} class="form">
      <p class="lede">
        We sent a 6-digit code to <strong>{step.email}</strong>. It expires in 5 minutes.
      </p>
      <label class="field">
        <span class="label">Code</span>
        <input
          bind:this={codeInputEl}
          type="text"
          bind:value={code}
          autocomplete="one-time-code"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="6"
          required
          disabled={busy}
          placeholder="123456"
        />
      </label>
      {#if error}
        <p class="error">{error}</p>
      {/if}
      <button class="btn btn-primary submit" type="submit" disabled={busy || code.trim().length !== 6}>
        {busy ? 'Verifying…' : 'Verify'}
      </button>
      <button class="btn btn-tertiary back" type="button" onclick={backToEmail} disabled={busy}>
        Use a different email
      </button>
    </form>
  {/if}
</ModalShell>

<style>
  .form { display: flex; flex-direction: column; gap: 1rem; }
  .lede { margin: 0; font-size: 14px; line-height: 1.5; color: var(--color-body); }
  .field { display: flex; flex-direction: column; gap: 0.375rem; }
  .label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  input {
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-sm);
    padding: 0.625rem 0.875rem;
    font-size: 16px;
    color: var(--color-ink);
    background: var(--color-canvas);
    font-family: inherit;
  }
  input:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px var(--color-ink);
  }
  .error { margin: 0; font-size: 13px; color: var(--color-error); }
  .submit { margin-top: 0.25rem; }
  .back { align-self: center; }
</style>
